import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronDown,
  faLock,
  faRotateLeft,
} from '@fortawesome/pro-regular-svg-icons';
import type { CarrierEligibility } from '../../../../../../../api/quotes.api';
import {
  CARRIER_STATUS_LABEL,
  countPassingRules,
  deriveCarrierStatus,
  isCarrierSelectable,
} from '../carrierEligibilityDisplay';
import CarrierEligibilityBreakdown from './CarrierEligibilityBreakdown';
import { getStatusTone } from './statusTone';

interface CarrierEligibilityRowProps {
  readonly option: CarrierEligibility;
  readonly selected: boolean;
  readonly onSelect: (treatyId: string) => void;
  readonly dark: boolean;
}

const ACTIVE_COLOR = 'var(--blue-blue-6)';

/**
 * One carrier in the eligibility dropdown (FPX-7027): a selectable row showing the
 * carrier, its status chip and pass count, plus a "View rules" toggle that expands
 * the full rule breakdown inline. Hard-blocked carriers render but cannot be picked.
 */
const CarrierEligibilityRow = ({
  option,
  selected,
  onSelect,
  dark,
}: CarrierEligibilityRowProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const status = deriveCarrierStatus(option);
  const tone = getStatusTone(status, dark);
  const selectable = isCarrierSelectable(option);
  const passed = countPassingRules(option.rules);
  const total = option.rules.length;

  return (
    <li style={{ listStyle: 'none' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          borderRadius: 6,
          backgroundColor: selected ? 'rgba(45, 140, 240, 0.1)' : 'transparent',
        }}
      >
        <button
          type="button"
          disabled={!selectable}
          onClick={() => onSelect(option.treatyId)}
          style={{
            display: 'flex',
            flex: 1,
            minWidth: 0,
            alignItems: 'center',
            gap: 10,
            padding: '6px 8px',
            border: 'none',
            background: 'none',
            textAlign: 'left',
            color: 'inherit',
            font: 'inherit',
            cursor: selectable ? 'pointer' : 'not-allowed',
            opacity: selectable ? 1 : 0.75,
            borderRadius: 6,
          }}
        >
          <span
            style={{
              width: 16,
              flexShrink: 0,
              display: 'inline-flex',
              justifyContent: 'center',
            }}
          >
            {selected ? (
              <FontAwesomeIcon
                icon={faCheck}
                style={{ color: ACTIVE_COLOR, fontSize: 13 }}
              />
            ) : null}
          </span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.carrierName}
              </span>
              {option.isAssigned ? (
                <span
                  style={{
                    flexShrink: 0,
                    borderRadius: 999,
                    backgroundColor: 'rgba(100, 116, 139, 0.85)',
                    color: '#fff',
                    padding: '1px 6px',
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  Assigned
                </span>
              ) : null}
            </span>
            <span
              style={{
                marginTop: 3,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  borderRadius: 999,
                  padding: '1px 7px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: tone.fg,
                  backgroundColor: tone.bg,
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    backgroundColor: tone.dot,
                  }}
                />
                {CARRIER_STATUS_LABEL[status]}
              </span>
              <span style={{ fontSize: 10, opacity: dark ? 0.7 : 0.55 }}>
                {passed}/{total} rules pass
              </span>
            </span>
          </span>
        </button>

        <button
          type="button"
          data-testid={`carrier-rules-${option.treatyId}`}
          aria-expanded={showDetails}
          onClick={() => setShowDetails((value) => !value)}
          style={{
            display: 'flex',
            flexShrink: 0,
            alignItems: 'center',
            gap: 4,
            padding: '0 8px',
            border: 'none',
            background: 'none',
            color: 'inherit',
            font: 'inherit',
            fontSize: 11,
            fontWeight: 500,
            opacity: 0.75,
            cursor: 'pointer',
            borderRadius: 6,
          }}
        >
          {showDetails ? 'Hide rules' : 'View rules'}
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{
              fontSize: 12,
              transform: showDetails ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          />
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateRows: showDetails ? '1fr' : '0fr',
          transition: 'grid-template-rows 0.18s ease',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <div
            style={{
              margin: '2px 4px 4px 32px',
              borderRadius: 6,
              backgroundColor: 'rgba(128, 128, 128, 0.08)',
              padding: '8px 10px',
            }}
          >
            {status !== 'applicable' ? (
              <OverrideBanner overridable={option.overridable} dark={dark} />
            ) : null}
            <CarrierEligibilityBreakdown rules={option.rules} dark={dark} />
          </div>
        </div>
      </div>
    </li>
  );
};

interface OverrideBannerProps {
  readonly overridable: boolean;
  readonly dark: boolean;
}

const OverrideBanner = ({ overridable, dark }: OverrideBannerProps) => {
  const tone = getStatusTone(overridable ? 'overridable' : 'unavailable', dark);

  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        marginBottom: 10,
        borderRadius: 6,
        borderLeft: `3px solid ${tone.dot}`,
        padding: '7px 9px',
        fontSize: 12,
        lineHeight: 1.4,
        // Inherit the surface text color so the body stays high-contrast in both
        // schemes; the status color lives on the icon + accent border, not the text
        // (colored text on a colored fill was unreadable in dark mode).
        color: 'inherit',
        backgroundColor: tone.bg,
      }}
    >
      <FontAwesomeIcon
        icon={overridable ? faRotateLeft : faLock}
        style={{ marginTop: 2, fontSize: 12, flexShrink: 0, color: tone.fg }}
      />
      <span>
        {overridable
          ? 'This carrier does not currently meet eligibility rules, but a manual override is available.'
          : 'This carrier does not meet eligibility rules and cannot be manually overridden.'}
      </span>
    </div>
  );
};

export default CarrierEligibilityRow;
